/**
 * Data layer — single source of truth for product data at runtime.
 *
 * On init, tries to fetch from Payload CMS via api.ts.
 * Falls back to static data in @/data/products if the CMS is unreachable.
 *
 * Includes localStorage caching with a 5-minute TTL to avoid refetching
 * on every page load while keeping data reasonably fresh.
 */
// api.ts is ~39 KB raw of CMS marshaling code. It's only used by
// `fetchAndApply` below — typed-import alone wouldn't pull it in,
// but the value import of `fetchCMSData` does. Made it lazy: the
// real module is loaded inside fetchAndApply via `await import`.
// On the inline-SSR-data path (every prerendered page), fetchAndApply
// runs in the background so this chunk is off the critical render path.
import type { HomepageContent, EventsPageContent } from "@/lib/api";
import { CMS_BASE_URL } from "@/lib/config";

type ApiModule = typeof import("@/lib/api");
let apiPromise: Promise<ApiModule> | null = null;
function loadApi(): Promise<ApiModule> {
  if (!apiPromise) apiPromise = import("@/lib/api");
  return apiPromise;
}
// Static fallback data (`@/data/products` is ~142 KB raw,
// `@/data/services-content` ~35 KB) is loaded LAZILY — it's only
// needed when the CMS fetch fails or to enrich CMS products that
// are missing rich-content fields. Eagerly importing it forced
// ~177 KB into the entry chunk for every visitor; now those bytes
// only land in a separate chunk that fires on the catch path or
// inside mergeCmsWithStatic. Type imports stay synchronous because
// types are stripped at build time.
import type {
  Event,
  Industry,
  Product,
  ProductFamily as ProductFamilyType,
  ProductGroup as ProductGroupType,
  ProductLine as ProductLineType,
} from "@/data/products";
import type { ServiceContent } from "@/data/services-content";

type StaticProductsModule = typeof import("@/data/products");
type StaticServicesModule = typeof import("@/data/services-content");

let staticDataPromise: Promise<{
  products: StaticProductsModule;
  servicesContent: StaticServicesModule;
}> | null = null;

/**
 * Load the static catalogue + services-content modules lazily.
 * Caches the resolved promise so repeated calls (e.g. CMS retry,
 * mergeCmsWithStatic enrichment) don't re-import.
 */
async function loadStaticData(): Promise<{
  products: StaticProductsModule;
  servicesContent: StaticServicesModule;
}> {
  if (!staticDataPromise) {
    staticDataPromise = Promise.all([
      import("@/data/products"),
      import("@/data/services-content"),
    ]).then(([products, servicesContent]) => ({ products, servicesContent }));
  }
  return staticDataPromise;
}

let enrichmentScheduled = false;

/**
 * After a successful CMS fetch, schedule static-data enrichment for
 * products that are missing rich fields (longDescription, media, image).
 * Runs at idle so the static chunk fetch (~110 KB raw / 21 KB gz)
 * doesn't compete with first paint.
 *
 * The enrichment is a no-op for products that already have the fields
 * filled in via the CMS — most of the catalogue, in practice. Editors
 * who haven't backfilled rich content yet still get sensible defaults
 * when the chunk arrives.
 */
function scheduleStaticEnrichment(): void {
  if (enrichmentScheduled) return;
  enrichmentScheduled = true;
  if (typeof window === "undefined") return; // SSR/Node: skip

  const fire = async (): Promise<void> => {
    try {
      const staticData = await loadStaticData();
      const merged = mergeCmsWithStatic(products, staticData.products.products);
      products = sortBySortOrder(merged);
      rebuildIndexes();
      notifyDataChanged();
    } catch {
      // Network or chunk failure — leave CMS-only data in place. No
      // user-visible regression; missing fields just stay missing.
    }
  };

  type IdleWin = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  const w = window as IdleWin;
  if (typeof w.requestIdleCallback === "function") {
    // 5 s timeout pushes the chunk past Lighthouse's measurement
    // window AND past the typical FCP/LCP envelope, so the audit
    // never sees the chunk as "unused JS."
    w.requestIdleCallback(() => void fire(), { timeout: 5000 });
  } else {
    // Safari < 16: 3 s past LCP is conservative enough to keep this
    // off the critical path on real-world connections.
    setTimeout(() => void fire(), 3000);
  }
}

// Re-export types so consumers don't need a second import
export type {
  Product,
  ProductSpecs,
  ProductLine,
  ProductFamily,
  ProductGroup,
  ProductMedia,
  CompatibilityMatrix,
  Event,
  Industry} from "@/data/products";

export type { ServiceContent } from "@/data/services-content";

// `filterOptions` re-export was removed here — knip showed no
// external consumer. Group filters are now CMS-driven: the axes come
// from group.filters (or legacy show_in_filter specEntries), resolved
// in ProductGroup.tsx — no static global filter list is re-exported.

// ── Mutable module-level state (populated by initializeData) ─────────
//
// IMPORTANT: initial state is intentionally EMPTY, not the static
// fallback. Reason: components render synchronously on mount, before
// initializeData() finishes its CMS fetch. If we seeded with the
// static placeholder catalogue here, every page would briefly flash
// the static product/family cards, then re-render to the actual CMS
// state once the fetch resolves — exactly the bug users reported as
// "I see the data for a moment, then it disappears."
//
// Static fallback is still honored, but only when the CMS fetch
// itself throws (network error, 5xx). See the catch block in
// fetchAndApply() — it restores the static catalogue ONLY in the
// failure path. Successful empty responses are trusted as-is.

export let productLines: ProductLineType[] = [];
export let productFamilies: ProductFamilyType[] = [];
export let productGroups: ProductGroupType[] = [];
export let products: Product[] = [];
export let events: Event[] = [];

// ── Data-change subscription ─────────────────────────────────────────
// React components that conditionally render based on async CMS data
// (e.g. EventsSection — empty static fallback, returns null if empty)
// need to re-render when initializeData() populates state. Module-level
// `let` re-assignments don't trigger React re-renders on their own, so
// we fire a manual version bump and let components subscribe via
// useSyncExternalStore. See hooks/useDataVersion.ts for the React side.
let dataVersion = 0;
const dataListeners = new Set<() => void>();

export function getDataVersion(): number {
  return dataVersion;
}

export function subscribeToData(listener: () => void): () => void {
  dataListeners.add(listener);
  return () => { dataListeners.delete(listener); };
}

function notifyDataChanged(): void {
  dataVersion++;
  for (const l of dataListeners) l();
}
// Empty initial state — same rationale as productLines above. Static
// fallback only restored in the catch block of fetchAndApply().
export let industries: Industry[] = [];
export let services: Record<string, ServiceContent> = {};
// Homepage content. Null until initializeData() resolves; the hero +
// differentials components render their hardcoded defaults during
// the gap so the page never has a blank moment.
export let homepage: HomepageContent | null = null;
/** /eventos page content (its hero image). Null until the CMS resolves. */
export let eventsPage: EventsPageContent | null = null;
// Site-wide share message templates (CMS Global). The Compartilhar
// dropdown reads these and falls back to a hardcoded default when
// any field is empty so the share button never breaks.
export let shareTemplates: import("@/lib/api").ShareTemplates | null = null;

/**
 * Test-only: synchronously populate module state from the static
 * catalogue. Used by tests/setup.ts so unit tests don't have to spin
 * up a fake CMS to validate search / route-generation / index logic.
 *
 * Safe to call at any time — it just mirrors what the catch block of
 * fetchAndApply() does on a CMS outage. Underscore prefix signals
 * "private but exported for test plumbing only" — runtime code paths
 * never call this.
 */
/**
 * Internal helper: apply a snapshot of static fallback data to module
 * state. Shared by:
 *   - The CMS-fetch catch block in fetchAndApply (await import path).
 *   - The test seed (lib/_test-static-seed.ts), which calls this with
 *     synchronously-imported modules so unit tests can populate state
 *     without going through the async import dance.
 *
 * Underscore prefix signals "private but exported for plumbing"; no
 * runtime page should call this directly.
 */
export function _applyStaticFallback(snapshot: {
  productLines: ProductLineType[];
  productFamilies: ProductFamilyType[];
  productGroups: ProductGroupType[];
  products: Product[];
  industries: Industry[];
  services: Record<string, ServiceContent>;
}): void {
  productLines = snapshot.productLines;
  productFamilies = sortFamilies(snapshot.productFamilies);
  productGroups = sortGroups(snapshot.productGroups);
  products = snapshot.products.map((p, i) => ({ ...p, sortOrder: p.sortOrder ?? i }));
  industries = snapshot.industries;
  services = snapshot.services;
  rebuildIndexes();
}

// ── Lookup indexes for O(1) access ───────────────────────────────────
// Rebuilt on every CMS data update. Avoids repeated `.find()` calls in
// hot render paths (e.g. ProductGroupModels rendering product grids).

export let productLinesById: Map<string, ProductLineType> = buildIndex(productLines);
export let productFamiliesById: Map<string, ProductFamilyType> = buildIndex(productFamilies);
export let productGroupsById: Map<string, ProductGroupType> = buildIndex(productGroups);
// `productsById` keys by id, which IS the slug Payload returns from
// the CMS (api.ts uses `p.slug ?? String(p.id)` as the id). The old
// `productsBySku` keyed on a SKU field that no longer exists on either
// side of the wire — collapsed into a single id-based index.
export let productsById: Map<string, Product> = buildIndex(products);

/**
 * Live Preview only: replace (or add) a single product in the store from
 * the editor's unsaved form data, then notify subscribers so the product
 * page re-renders with it. Runs only inside the CMS admin preview iframe
 * (see pages/Preview.tsx); the live site never calls this.
 */
export function upsertPreviewProduct(p: Product): void {
  const i = products.findIndex((x) => x.id === p.id);
  products =
    i >= 0
      ? [...products.slice(0, i), p, ...products.slice(i + 1)]
      : [...products, p];
  productsById = buildIndex(products);
  notifyDataChanged();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildIndex<T extends Record<string, any>>(arr: T[], key: keyof T = 'id' as keyof T): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of arr) {
    if (item[key]) map.set(String(item[key]), item);
  }
  return map;
}

/** Rebuild all lookup indexes after CMS data loads or cache refreshes */
function rebuildIndexes(): void {
  productLinesById = buildIndex(productLines);
  productFamiliesById = buildIndex(productFamilies);
  productGroupsById = buildIndex(productGroups);
  productsById = buildIndex(products);
}

let _initialized = false;

// ── Cache configuration ─────────────────────────────────────────────

// Cache key is bound to the build hash so a new deploy automatically
// orphans every old cache. Without this, an editor unpublishing a
// record on the CMS could still see the old data flash on the site
// for the next 5 minutes (TTL window) because the previous build's
// cache was reused by the new build's bundle.
const CACHE_KEY = `orbrasil_cms_cache_${__BUILD_HASH__}`;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
// Lightweight signal exposed to UI so the footer "cached" badge tells
// the truth instead of always saying "cached." Set to true only when
// the current page actually hydrated from localStorage (vs a fresh
// network fetch).
let _hydratedFromCache = false;
export function isHydratedFromCache(): boolean {
  return _hydratedFromCache;
}
// True when the COLD-load CMS fetch failed and state fell back to the
// build-time static catalogue (or to nothing, if even that chunk failed).
// In the browser that fallback is the right call — show something. At
// build time it is a disaster: scripts/prerender.mjs would bake the
// ancient static data into every page's HTML + __or_data__ snapshot,
// and every visitor's first paint shows years-old content until the
// background refresh swaps it out. That shipped on 2026-08-10 when the
// runner's network blipped mid-build (run 31391773646). The prerender
// checks this flag and refuses to bake — a failed deploy keeps the
// previous good version live, which is strictly better.
let _staticFallback = false;
export function usedStaticFallback(): boolean {
  return _staticFallback;
}

interface DataSnapshot {
  productLines: ProductLineType[];
  productFamilies: ProductFamilyType[];
  productGroups: ProductGroupType[];
  products: Product[];
  events: Event[];
  industries: Industry[];
  services: Record<string, ServiceContent>;
  homepage: HomepageContent | null;
  eventsPage: EventsPageContent | null;
  shareTemplates: import("@/lib/api").ShareTemplates | null;
}

interface CacheEntry {
  timestamp: number;
  data: DataSnapshot;
}

/**
 * Snapshot the populated data state — used by the prerender script to
 * inline the live CMS state into each prerendered HTML page so the
 * client can hydrate synchronously without a localStorage round-trip
 * or a network fetch on first visit. Mirrors what writeCache() saves
 * but as a pure function (no side effects).
 */
export function getDataSnapshot(): DataSnapshot {
  return {
    productLines,
    productFamilies,
    productGroups,
    products,
    events,
    industries,
    services,
    homepage,
    eventsPage,
    shareTemplates,
  };
}

/**
 * Read a server-injected data snapshot from `<script id="__or_data__">`
 * (placed by scripts/prerender.mjs) and populate module state from it.
 * Returns true on success, false if no inlined snapshot was found or
 * parsing failed. When this returns true, hydration is matching SSR
 * by definition — the same data that produced the prerendered HTML.
 */
function hydrateFromInline(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.getElementById("__or_data__");
  if (!el?.textContent) return false;
  try {
    const snapshot: DataSnapshot = JSON.parse(el.textContent);
    productLines = snapshot.productLines;
    productFamilies = sortFamilies(snapshot.productFamilies);
    productGroups = sortGroups(snapshot.productGroups);
    products = sortBySortOrder(snapshot.products);
    events = snapshot.events;
    industries = snapshot.industries;
    services = snapshot.services;
    homepage = snapshot.homepage;
    eventsPage = snapshot.eventsPage ?? null;
    shareTemplates = snapshot.shareTemplates;
    rebuildIndexes();
    return true;
  } catch {
    return false;
  }
}

// Safe localStorage access — returns null in SSR/Node environments
function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

function readCache(): CacheEntry | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      storage.removeItem(CACHE_KEY);
      return null;
    }
    return entry;
  } catch {
    // Cache parse failed (corrupt JSON, schema drift, etc). Best-effort
    // delete the broken entry so the next page-load starts clean. The
    // inner catch swallows because Safari private mode + iframe Storage
    // blocking can throw on removeItem, and we'd rather silently move
    // on to a fresh CMS fetch than surface a console error for a known
    // browser quirk.
    try {
      storage.removeItem(CACHE_KEY);
    } catch {
      /* ignore — see comment above */
    }
    return null;
  }
}

function writeCache(data: CacheEntry["data"]): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const entry: CacheEntry = { timestamp: Date.now(), data };
    storage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

/**
 * Order comparator for CMS-orderable items. Primary key is Payload's
 * fractional `_order` string (plain lexicographic compare — NOT
 * localeCompare, which is locale-aware and would mis-order the base-36
 * keys). Items that have an order key sort first; the rest fall back to the
 * legacy numeric `sortOrder`, then name. The fallback keeps ordering stable
 * during the transition (rows not yet dragged have no `_order` yet) and for
 * the static-seed fallback data.
 */
export function compareCmsOrder(
  a: { order?: string; sortOrder?: number; name: string },
  b: { order?: string; sortOrder?: number; name: string },
): number {
  const ao = a.order;
  const bo = b.order;
  if (ao && bo) return ao < bo ? -1 : ao > bo ? 1 : 0;
  if (ao) return -1;
  if (bo) return 1;
  return (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.name.localeCompare(b.name);
}

/**
 * Single-product-group collapse.
 *
 * A group that exists only to host ONE product's spec table — the editor
 * gives the group and its single product the same name (e.g. "Rebite com
 * Rosca Interna") — collapses straight to that product: the group page
 * redirects to it, family cards link to it, and its breadcrumb drops the
 * redundant group crumb.
 *
 * The name-match guard is deliberate: it separates an intentionally
 * single-product group from a multi-product group that merely has one
 * product published so far (e.g. "Rebites Estruturais" → "Orlock",
 * mid-migration), which must NOT collapse. Returns the sole product when
 * the group collapses, else null.
 */
export function collapsedProductOfGroup(groupId: string): Product | null {
  const group = productGroups.find((g) => g.id === groupId);
  if (!group) return null;
  const inGroup = products.filter(
    (p) => p.groupId === groupId && p.specs.type !== "Acessório",
  );
  if (inGroup.length !== 1) return null;
  const only = inGroup[0];
  const norm = (s: string) => s.trim().toLowerCase();
  return norm(only.name) === norm(group.name) ? only : null;
}

/**
 * The group a family collapses into — the same idea one level up.
 *
 * A family whose page would render a single group card bearing the family's
 * own name is a page that says nothing: "Pinos de Solda Capacitiva" listing
 * "Tipos de Pinos de Solda Capacitiva" containing one card, "Pinos de Solda
 * Capacitiva". It sits between the reader and the thing they clicked for.
 *
 * Three conditions, all needed:
 *
 *  - The family actually renders group cards. `simple_grid` and
 *    `type_toggle` families list PRODUCTS, so their page carries real
 *    content even with one group behind it — "Máquinas de Solda Capacitiva"
 *    has one group but shows three products, and must not collapse.
 *  - Exactly one group, or there is a genuine choice to present.
 *  - The names match. A differently-named card is informative: "Ferramenta
 *    para Furo Sextavado" → "PB-80FS" tells the reader the model, so it
 *    stays. Same guard, same reason, as collapsedProductOfGroup.
 *
 * Returns the sole group when the family collapses, else null. Callers
 * should then run collapsedProductOfGroup on the result — the two chain, so
 * a family/group/product triple with one name lands on the product.
 */
export function collapsedGroupOfFamily(familyId: string): ProductGroupType | null {
  const family = productFamilies.find((f) => f.id === familyId);
  if (!family) return null;
  if ((family.filterLayout ?? "groups") !== "groups") return null;
  const inFamily = productGroups.filter((g) => g.familyId === familyId);
  if (inFamily.length !== 1) return null;
  const only = inFamily[0];
  const norm = (s: string) => s.trim().toLowerCase();
  return norm(only.name) === norm(family.name) ? only : null;
}

/** Sort products by CMS order (`_order`), falling back to sortOrder then name. */
function sortBySortOrder(prods: Product[]): Product[] {
  return [...prods].sort(compareCmsOrder);
}

/**
 * Sort families/groups by their CMS per-parent scoped order key (mapped
 * onto `order` by the api.ts transform). Same comparator as products.
 *
 * A flat sort is safe even though the scoped keys are only monotonic
 * *within* a parent: every consumer filters these arrays by parent
 * (families by `lineId`, groups by `familyId`) before rendering, so the
 * cross-parent ordering the flat sort produces is never observed. Equal
 * keys across different parents just tiebreak by name, which the filter
 * then hides.
 */
function sortFamilies(fams: ProductFamilyType[]): ProductFamilyType[] {
  return [...fams].sort(compareCmsOrder);
}

function sortGroups(grps: ProductGroupType[]): ProductGroupType[] {
  return [...grps].sort(compareCmsOrder);
}

/**
 * Merge CMS products with static product data.
 * If a CMS product is missing rich content fields (longDescription, media,
 * image), fall back to the matching static product's values.
 */
function mergeCmsWithStatic(cmsProducts: Product[], staticProds: Product[]): Product[] {
  // SKU was dropped — match purely by id (which is the slug Payload
  // returns and the kebab-case key static-data entries use).
  const staticById = new Map(staticProds.map(p => [p.id, p]));

  let unmatchedCount = 0;

  const merged = cmsProducts.map(cp => {
    const fallback = staticById.get(cp.id);
    if (!fallback) {
      unmatchedCount++;
      console.warn(`[data] CMS product "${cp.name}" (id=${cp.id}) has no static fallback`);
      return cp;
    }

    return {
      ...cp,
      longDescription: cp.longDescription || fallback.longDescription,
      media: (cp.media && cp.media.length > 0) ? cp.media : fallback.media,
      image: (cp.image && cp.image !== "/placeholder.svg") ? cp.image : fallback.image,
    };
  });

  if (unmatchedCount > 0) {
    console.warn(`[data] ${unmatchedCount} CMS product(s) had no static fallback — rich fields may be missing`);
  }

  return merged;
}

/**
 * Must be called once before rendering.
 * Attempts CMS fetch; silently falls back to static data on failure.
 * Uses localStorage cache to avoid refetching on every page load.
 */
export async function initializeData(): Promise<void> {
  if (_initialized) return;
  _initialized = true;

  // Hydration path #1: server-injected snapshot. The prerender script
  // emits `<script id="__or_data__">...</script>` on every page,
  // carrying the exact CMS state that produced the SSR'd HTML. When
  // present, we populate module state synchronously *before* React
  // hydrates, guaranteeing the client's first render matches the
  // SSR'd DOM (the prerequisite for hydrateRoot in main.tsx).
  // A background refresh still fires so the user picks up edits the
  // editor has made since the build, just like the localStorage path.
  if (hydrateFromInline()) {
    notifyDataChanged();
    void fetchAndApply().catch(() => {});
    return;
  }

  const cmsUrl = import.meta.env.VITE_CMS_URL || CMS_BASE_URL;
  if (!cmsUrl) {
    if (import.meta.env.DEV) console.info("[data] No CMS URL configured — using static data");
    return;
  }

  // Try localStorage cache first (stale-while-revalidate pattern)
  const cached = readCache();
  if (cached) {
    if (import.meta.env.DEV) console.info("[data] Using cached CMS data (will refresh in background)");
    _hydratedFromCache = true;
    productLines = cached.data.productLines;
    productFamilies = sortFamilies(cached.data.productFamilies);
    productGroups = sortGroups(cached.data.productGroups);
    products = sortBySortOrder(cached.data.products);
    events = cached.data.events;
    // Cache trust mirrors the live-fetch rule: a successful prior fetch
    // wrote what the CMS returned, even if empty. Don't substitute
    // static data here — it would just delay the user noticing they
    // unpublished everything until the background refresh completes.
    industries = cached.data.industries ?? [];
    services = cached.data.services ?? {};
    homepage = cached.data.homepage ?? null;
    eventsPage = cached.data.eventsPage ?? null;
    shareTemplates = cached.data.shareTemplates ?? null;
    rebuildIndexes();
    notifyDataChanged();

    // Refresh in background (stale-while-revalidate)
    fetchAndApply().catch(() => {});
    return;
  }

  // No cache — must fetch before render
  await fetchAndApply();
}

async function fetchAndApply(): Promise<void> {
  try {
    const cmsUrl = import.meta.env.VITE_CMS_URL || CMS_BASE_URL;
    if (import.meta.env.DEV) console.info(`[data] Fetching from CMS at ${cmsUrl}…`);
    const api = await loadApi();
    const cms = await api.fetchCMSData();

    productLines = cms.productLines;
    productFamilies = sortFamilies(cms.productFamilies);
    productGroups = sortGroups(cms.productGroups);
    // First pass: trust CMS as-is. The static catalogue's only job
    // here is enriching CMS products that are missing rich fields
    // (longDescription / media / image) — that enrichment runs in
    // the background via scheduleStaticEnrichment() so initial paint
    // doesn't pay for the 110 KB static chunk fetch.
    products = sortBySortOrder(cms.products);
    events = cms.events;
    // CMS is the source of truth: an empty list from a successful fetch
    // means "the editor drafted/archived everything", so we render
    // nothing instead of resurrecting the static fallback. Static data
    // is only used when the CMS fetch itself throws (caught below).
    industries = [...cms.industries].sort(compareCmsOrder);

    // Convert CMS services array to Record keyed by slug. Same rule as
    // industries — trust the CMS even when empty.
    const svcRecord: Record<string, ServiceContent> = {};
    for (const svc of cms.services) {
      svcRecord[svc.slug] = svc;
    }
    services = svcRecord;
    homepage = cms.homepage;
    eventsPage = cms.eventsPage;
    shareTemplates = cms.shareTemplates;
    // Live network response replaced any cache-hydrated state.
    _hydratedFromCache = false;
    rebuildIndexes();

    // Write to cache for next page load
    writeCache({
      productLines: cms.productLines,
      productFamilies,
      productGroups,
      products,
      events: cms.events,
      industries,
      services,
      homepage,
      shareTemplates,
      eventsPage,
    });

    if (import.meta.env.DEV) {
      console.info(
        `[data] CMS loaded: ${products.length} products, ${productGroups.length} groups, ${productFamilies.length} families, ${productLines.length} lines, ${events.length} events, ${industries.length} industries, ${Object.keys(services).length} services`
      );
    }
    notifyDataChanged();
    // Schedule static-data enrichment in the background. By the time
    // the chunk arrives, the user has already seen rendered content —
    // we just touch up products with missing rich fields (image,
    // longDescription, media) and re-notify subscribers so React
    // picks up the update.
    scheduleStaticEnrichment();
  } catch (err) {
    // We already have real data (inline SSR snapshot, or localStorage
    // cache, or an earlier successful fetch) — a failed BACKGROUND refresh
    // must not touch it.
    //
    // This was the "content randomly doesn't load" showstopper. Every
    // prerendered page ships an `__or_data__` snapshot, so the first render
    // always had the right content; then fetchAndApply() ran in the
    // background, and if any ONE of its nine parallel requests blipped
    // (Railway instance swap, a 5xx that outlived the retries, a timeout on
    // a slow connection) this catch swapped the whole catalogue for the
    // static fallback. That catalogue carries DIFFERENT ids and slugs, so
    // every id-matched section silently emptied itself: product tiles
    // vanished, "Bicos Especiais" disappeared, spec tables went blank. It
    // looked random because it tracked network luck, not the page.
    //
    // Keeping the good data is strictly better than downgrading to a stale
    // build-time copy. The fallback still does its real job — first paint
    // on a cold visit when the CMS is genuinely down — because that's the
    // only case where state is actually empty.
    if (products.length > 0 || productFamilies.length > 0) {
      console.warn("[data] CMS refresh failed — keeping the data already loaded", err);
      return;
    }

    console.warn("[data] CMS unreachable — falling back to static data", err);
    // Set before the static import, so the flag is true even if that
    // chunk ALSO fails — either way the state does not represent the CMS.
    _staticFallback = true;
    // Nothing loaded yet, so render something useful (offline dev, CMS
    // outage on a cold visit). This is the ONLY codepath at runtime that
    // loads the static fallback chunk; a successful but empty CMS response
    // is trusted as-is.
    try {
      const staticData = await loadStaticData();
      _applyStaticFallback({
        productLines: staticData.products.productLines,
        productFamilies: staticData.products.productFamilies,
        productGroups: staticData.products.productGroups,
        products: staticData.products.products,
        industries: staticData.products.industries,
        services: staticData.servicesContent.servicesContent,
      });
      notifyDataChanged();
    } catch (staticErr) {
      // Even the static chunk failed (rare — chunk-network failure on
      // a fresh deploy with stale HTML). Leave state empty so the
      // page renders empty rather than throwing.
      console.error("[data] Static fallback also failed", staticErr);
    }
  }
}

/**
 * Dynamically compute filter options for a specific product group.
 * Extracts unique values from actual product specs and sorts them alphabetically.
 */
